import React from 'react';
import useScrollAnimation from '../../hooks/useScrollAnimation';

const Bio = () => {
  // Scroll animations
  const { ref: bioRef, isVisible: bioVisible } = useScrollAnimation();
  const { ref: servicesRef, isVisible: servicesVisible } = useScrollAnimation({ threshold: 0.2 });

  return (
    <section className="pt-24 md:pt-28 pb-20 bg-white">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          {/* Main Bio Section with fade-in */}
          <div
            ref={bioRef}
            className={`grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center mb-20 md:mb-28 transition-all duration-700 ease-out ${
              bioVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
          >
            {/* Headshot Image */}
            <div className="order-2 lg:order-1">
              <div className="aspect-[3/4] bg-gradient-to-br from-accent-light via-primary-warm to-accent-alt/20 rounded-sm overflow-hidden shadow-soft-xl">
                <img
                  src="https://res.cloudinary.com/diaz39dwx/image/upload/v1762797016/ky-portfolio/lqavlrlsuybblu8ghuw1.jpg"
                  alt="Ky Tracy - Photographer and Creative Communicator"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>

            {/* Bio content */}
            <div className="order-1 lg:order-2">
              <h1 className="text-4xl md:text-5xl lg:text-display font-heading font-bold text-secondary mb-8 leading-tight">
                About Ky
              </h1>

              <div className="space-y-6 font-body text-text-primary text-lg leading-relaxed">
                <p>
                  Hi! I'm Ky, a photographer, writer, and creative communicator who believes in the power of authenticity. I'm a Communications student at Elizabethtown College, about 30 minutes from Harrisburg, though a piece of my heart will always be in Kennett Square, PA.
                </p>

                <p>
                  My photography is about capturing people as they truly are—beautiful, confident, and full of life. I aim to help you see the version of yourself that you want to share. Together, we can create images that play with bold colors or soft, glowing light. My goal is to capture your emotion and authenticity while making the experience comfortable and enjoyable.
                </p>

                <p>
                  While photography is the heart of my work, I'm also passionate about storytelling in all its forms—from writing to social media and design. I'm constantly exploring new ways to blend visuals and words to tell meaningful stories that resonate.
                </p>

                <p>
                  At the end of the day, I just want to create something that feels real. If it makes someone feel something, that's when I know I've done my job well. If that speaks to you, reach out. I'd love to hear from you!
                </p>
              </div>
            </div>
          </div>

          {/* Services Section with stagger animation */}
          <div
            ref={servicesRef}
            className={`transition-all duration-700 ease-out ${
              servicesVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
          >
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold text-secondary mb-12 md:mb-16 text-center">
              Services Offered
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 lg:gap-12">
              {[
                {
                  icon: '📸',
                  title: 'Photography',
                  description: 'Professional portraits that capture your unique essence and confidence',
                  delay: 0,
                },
                {
                  icon: '✍️',
                  title: 'Writing',
                  description: 'Compelling content and storytelling that resonates with your audience',
                  delay: 150,
                },
                {
                  icon: '📱',
                  title: 'Social Media',
                  description: 'Strategic social media management to grow your online presence',
                  delay: 300,
                },
              ].map((service, index) => (
                <div
                  key={service.title}
                  className={`text-center p-8 bg-bg-secondary rounded-sm shadow-soft hover:shadow-soft-lg transition-all duration-500 ${
                    servicesVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
                  }`}
                  style={{
                    transitionDelay: servicesVisible ? `${service.delay}ms` : '0ms',
                  }}
                >
                  <div className="text-6xl mb-6">{service.icon}</div>
                  <h3 className="font-heading font-bold text-xl lg:text-2xl text-secondary mb-4">
                    {service.title}
                  </h3>
                  <p className="font-body text-text-secondary leading-relaxed">
                    {service.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Bio;
